<script setup lang="ts">
import type { Category } from '../../../../../websites/types/AccommodationSearch'

interface Props {
  discountPercentage?: number
  beforePrice?: number
  price?: number
  currency?: string
  accommodationCategories?: Category[]
}

defineProps<Props>()

const searchStore = useSearchv2Store()

const emits = defineEmits(['view-accommodations', 'update-filters'])
</script>

<template>
  <div
    class="flex flex-col gap-y-4 w-full bg-brand-secondary-900 px-5 sm:px-3 py-5 lg:gap-y-6 shadow-custom"
  >
    <h4 class="text-center font-h4 text-brand-white hidden sm:block">
      {{ $t('search.search-and-book') }}
    </h4>

    <div class="hidden lg:flex flex-col gap-y-2">
      <Calender @apply-search="emits('update-filters')" />
      <PersonPicker @apply-search="emits('update-filters')" />
    </div>

    <div class="flex flex-col gap-y-4">
      <VButton
        wide
        size="md"
        class="!text-lg"
        @click="emits('view-accommodations')"
      >
        {{ $t('accommodation.accommodation-and-prices.show-accommodations') }}
      </VButton>

      <div class="bg-brand-white p-2.5 mt-2">
        <div
          class="h-14 w-14 translate-y-5 rotate-45 -mt-8 mx-auto bg-brand-white"
        />
        <div
          class="bg-brand-secondary-100 px-2 py-4 flex flex-col relative border -mt-6 border-brand-secondary-200"
        >
          <div v-if="price" class="flex flex-col gap-y-1 items-center mx-auto">
            <PriceFrom class="text-brand-success-500" />

            <Price :price="formatCurrency(price, currency)" />
          </div>

          <div class="flex flex-col gap-y-4">
            <div
              class="flex justify-center items-start font-p12 text-brand-gray-500 mt-2"
            >
              <Icon
                class="text-brand-success-500"
                size="22"
                name="material-symbols:check-rounded"
              />

              <p class="whitespace-pre-line">
                {{
                  searchStore.getSearchSummary(
                    searchStore.searchParameters.parameters.value
                      .date as string,
                    searchStore.searchParameters.parameters.value.duration,
                    searchStore.getNumberOfAdults,
                    searchStore.getNumberOfChildren
                  )
                }}
              </p>
            </div>

            <Divider class="border-brand-secondary-200" />

            <div
              v-if="accommodationCategories"
              class="font-p14 text-center mx-auto text-brand-gray-500"
            >
              <span class="text-brand-success-500 mr-2">
                {{ accommodationCategories.length }}
              </span>
              <span>
                {{ $t('site.accommodation-types-available') }}
              </span>
            </div>

            <div class="flex flex-wrap gap-x-2 justify-center">
              <VButton
                v-for="(category, index) in accommodationCategories"
                :key="index"
                variant="unstyled"
                class="text-brand-secondary-400 w-fit font-p12"
                @click="emits('view-accommodations')"
              >
                {{ category.category }} ({{ category.accommodationCount }})
              </VButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
